import { useState, type FormEvent } from "react";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Logo } from "../components/Logo";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { Button } from "../components/ui/Button";
import { Field, TextInput } from "../components/ui/Input";
import { signIn, signUp } from "../lib/authClient";
import { qk } from "../lib/hooks";

export type Mode = "signin" | "register";

/**
 * Real advisor auth against better-auth (`/api/auth/*`, same backend as the
 * consumer app). Toggles between sign-in and register; on success it clears the
 * cached `me`/`org` so the post-login app refetches with the new session, then
 * routes to the requested page (default `/app`).
 *
 * Note: with VITE_REQUIRE_AUTH off (the demo default) `/app` is reachable
 * without logging in — this screen is the path to a *real* session/workspace.
 */
export function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();
  const { t } = useTranslation();
  const next = new URLSearchParams(location.search).get("next") || "/app";

  const [mode, setMode] = useState<Mode>("signin");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const res =
        mode === "signin"
          ? await signIn.email({ email, password })
          : await signUp.email({ email, password, name: name.trim() || email });

      if (res.error) {
        setError(authMessage(res.error, mode, t));
        return;
      }
      // New session — drop any stale identity caches so the guard/Topbar reload.
      await qc.invalidateQueries({ queryKey: qk.me });
      // Verified-email gate: a fresh signup (and any account that hasn't
      // confirmed yet) goes to /verify-email rather than into the workspace.
      if (res.data?.user?.emailVerified === false) {
        navigate(`/verify-email?email=${encodeURIComponent(email)}`, { replace: true });
        return;
      }
      navigate(next, { replace: true });
    } catch {
      setError(authMessage(undefined, mode, t));
    } finally {
      setBusy(false);
    }
  }

  const isRegister = mode === "register";

  return (
    <div className="relative grid min-h-screen place-items-center bg-surface-0 px-4">
      <div
        className="pointer-events-none absolute left-1/2 top-0 h-[40vh] w-[60vw] -translate-x-1/2 rounded-full opacity-30 blur-3xl"
        style={{
          background:
            "radial-gradient(closest-side, rgba(16,118,82,0.40), transparent)",
        }}
      />
      <div className="relative w-full max-w-sm">
        <div className="mb-6 flex flex-col items-center gap-4">
          <Link to="/">
            <Logo />
          </Link>
          <LanguageSwitcher />
        </div>
        <div className="card animate-scale-in p-6 shadow-pop">
          <h1 className="text-lg font-semibold text-ink">
            {isRegister ? t("login.createTitle") : t("login.signInTitle")}
          </h1>
          <p className="mt-1 text-sm text-ink-muted">
            {isRegister ? t("login.createSubtitle") : t("login.signInSubtitle")}
          </p>
          <form onSubmit={onSubmit} className="mt-5 space-y-4">
            {isRegister && (
              <Field label={t("login.yourName")}>
                <TextInput
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Lotte Vermeer"
                  autoComplete="name"
                />
              </Field>
            )}
            <Field label={t("login.workEmail")}>
              <TextInput
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t("login.emailPlaceholder")}
                autoComplete="email"
                required
              />
            </Field>
            <Field label={t("login.password")}>
              <TextInput
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                autoComplete={isRegister ? "new-password" : "current-password"}
                required
                minLength={8}
              />
            </Field>
            {!isRegister && (
              <p className="-mt-2 text-right text-xs">
                <Link to="/forgot-password" className="font-medium text-ink-muted hover:text-brand hover:underline">
                  {t("login.forgotPassword")}
                </Link>
              </p>
            )}
            {error && (
              <p className="rounded-md border border-status-lost/30 bg-status-lost/10 px-3 py-2 text-sm text-status-lost">
                {error}
              </p>
            )}
            <Button
              type="submit"
              variant="primary"
              className="w-full"
              disabled={busy}
            >
              {busy ? "…" : isRegister ? t("login.createAccount") : t("login.continue")}
            </Button>
          </form>

          <p className="mt-4 text-center text-sm text-ink-muted">
            {isRegister ? t("login.alreadyHaveAccount") : t("login.noAccountYet")}{" "}
            <button
              type="button"
              className="font-medium text-brand hover:underline"
              onClick={() => {
                setMode(isRegister ? "signin" : "register");
                setError(null);
              }}
            >
              {isRegister ? t("login.signInTitle") : t("login.createOne")}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}

/** Map a better-auth error (or a thrown failure) to a short, friendly line.
 *  Known codes get a localized message — better-auth's raw `message` is
 *  English and must never leak into the Dutch UI. */
export function authMessage(
  err: { code?: string; message?: string } | undefined,
  mode: Mode,
  t: TFunction,
): string {
  switch (err?.code) {
    case "INVALID_EMAIL_OR_PASSWORD":
      return t("login.errSignin");
    case "USER_ALREADY_EXISTS":
    // better-auth ≥1.6 returns this variant on sign-up.
    case "USER_ALREADY_EXISTS_USE_ANOTHER_EMAIL":
      return t("login.errExists");
    case "PASSWORD_TOO_SHORT":
    case "PASSWORD_TOO_LONG":
      return t("login.errPasswordLength");
    default:
      return mode === "register" ? t("login.errCreate") : t("login.errSignin");
  }
}
